import { valueOf } from "@/types/common";
import { convertByteToMb } from "@/utils/file";

export const FILE_STATUS = {
  IN_PROGRESS: 0,
  OK: 1,
  ERROR: 2,
  ERROR_MIN_SIZE: 3,
  ERROR_MAX_SIZE: 4,
  ERROR_ACCEPT_FILES: 5,
};

export type TFileStatus = valueOf<typeof FILE_STATUS>;

export const FILE_ERROR_MESSAGE = {
  [FILE_STATUS.ERROR]: "FILE is invalid",
  [FILE_STATUS.ERROR_MAX_SIZE]: "ファイルサイズは{{maxSize}}以内でお願いします。",
  [FILE_STATUS.ERROR_MIN_SIZE]: "lt {{minSize}}。",
  [FILE_STATUS.ERROR_ACCEPT_FILES]: "ファイル形式は{{extensions}}にてお願いします。",
};

export const FILE_UPLOAD_FOR = {
  INFORMATION_FILE: "INFORMATION_FILE",
  CHAT_FILE: "CHAT_FILE",
  CONVERT_REQUEST_FILE: "CONVERT_REQUEST_FILE",
  PAYROLL_FILES: "PAYROLL_FILES",
  PAYCHECK_FILE: "PAYCHECK_FILE",
  RSO_ACCOUNT_FILE: "RSO_ACCOUNT_FILE",
  TALENT_AVATAR_FILE: "TALENT_AVATAR_FILE",
  TALENT_AVATAR_FILE_RESIZE: "TALENT_AVATAR_FILE_RESIZE",
  TALENT_AVATAR_THUMBNAIL_FOR_FORM: "TALENT_AVATAR_THUMBNAIL_FOR_FORM",
  TALENT_AVATAR_THUMBNAIL_FOR_LIST: "TALENT_AVATAR_THUMBNAIL_FOR_LIST",
  TALENT_CONTRACT_CONDITION_FILE: "TALENT_CONTRACT_CONDITION_FILE",
  TALENT_CONTRACT_TALENT_FILE: "TALENT_CONTRACT_TALENT_FILE",
  TALENT_PASSPORT_FILE: "TALENT_PASSPORT_FILE",
  TALENT_RESIDENCE_CARD_BACK_FILE: "TALENT_RESIDENCE_CARD_BACK_FILE",
  TALENT_RESIDENCE_CARD_FRONT_FILE: "TALENT_RESIDENCE_CARD_FRONT_FILE",
  TALENT_CAREER_TRAINING_COMPLETION_FILE: "TALENT_CAREER_TRAINING_COMPLETION_FILE",
  TALENT_CAREER_SKILL_COMPLETION_FILE: "TALENT_CAREER_SKILL_COMPLETION_FILE",
  TALENT_CAREER_JP_COMPLETION_1_FILE: "TALENT_CAREER_JP_COMPLETION_1_FILE",
  TALENT_CAREER_JP_COMPLETION_2_FILE: "TALENT_CAREER_JP_COMPLETION_2_FILE",
  TALENT_CAREER_CONFIRM_GUIDE_FILE: "TALENT_CAREER_CONFIRM_GUIDE_FILE",
  TALENT_CAREER_CONFIRM_ORI_FILE: "TALENT_CAREER_CONFIRM_ORI_FILE",
  TALENT_CAREER_DEPEND_FAMILY_FILE: "TALENT_CAREER_DEPEND_FAMILY_FILE",
  TALENT_CAREER_DEPEND_REMIT_FILE: "TALENT_CAREER_DEPEND_REMIT_FILE",
  CONTACT_FILE: "CONTACT_FILE",
  ANY_TIME_FILES: "ANY_TIME_FILES",
  RENEW_VISA_FILES: "RENEW_VISA_FILES",
  COE_FILES: "COE_FILES",
  COE_PDF_FILES: "COE_PDF_FILES",
  RETIREMENT_FILE: "RETIREMENT_FILE",
  MATCH_LICENSE_FILE: "MATCH_LICENSE_FILE",
  MATCH_OFFICE_APPOINTMENT_FILE: "MATCH_OFFICE_APPOINTMENT_FILE",
};

export type TFileUploadFor = valueOf<typeof FILE_UPLOAD_FOR>;

export const MIME_TYPES = {
  IMAGE: ["image/jpg", "image/jpeg", "image/png", "image/gif"],
  TIFF: ["image/tif", "image/tiff"],
  HEIC: ["image/heic", "image/heif"],
  PDF: ["application/pdf"],
  EXCEL: ["application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "text/csv"],
  WORD: ["application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/x-ole-storage"],
  TXT: ["text/plain"],
  CSV: ["text/csv"],
  XML: ["application/xml", "text/xml"],
  JPEG: ["image/jpg", "image/jpeg"],
  ZIP: ["application/zip"],
  STACK: ["application/vnd.rar", "application/x-rar-compressed", "application/x-rar", "multipart/x-rar", ".rar", "rar"],
  PPT: ["application/vnd.ms-powerpoint", "application/vnd.openxmlformats-officedocument.presentationml.presentation"],
};

export const UPLOAD_COE_MIME_TYPES = [".pdf", ".doc", ".docx", ".xls", ".xlsx"];
export const ONLY_PDF_MIME_TYPES = [".pdf"];

export const EXTENSION_NAMES: Record<string, string> = {
  IMAGE: "Image",
  HEIC: "HEIC",
  PDF: "PDF",
  EXCEL: "Excel",
  WORD: "Word",
  TXT: "Txt",
  CSV: "CSV",
  XML: "XML",
  JPEG: "Jpeg",
  ZIP: "Zip",
  RAR: "RAR",
};

export const EXTENSIONS = {
  IMAGE: [".jpg", ".jpeg", ".png"],
  PDF: [".pdf"],
  WORD: [".doc", ".docx"],
  ZIP: [".zip"],
  EXCEL: [".xlsx", ".csv"],
  XML: [".xml"],
  HEIC: [".heic"],
  TXT: [".txt"],
  CSV: [".csv"],
  JPEG: [".jpeg"],
  STACK: [".rar"],
};

export const RATIO_FILE_UPLOAD = {
  minRatio: 2 / 3,
  maxRatio: 7 / 8,
};

export const FILE_EXISTED = "true";
export const ACCEPT_ERROR = (extension: string[]) => `アップロード可能なファイル形式は${extension.join("、")} です。`;
export const MAX_SIZE_ERROR = (maxSize: number) => `ファイルサイズは${convertByteToMb(maxSize)}MB以内でお願いします。`;

export const COMMON_FILE_TYPES_EXTENSION = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".csv",
  ".ppt",
  ".pptx",
  ".txt",
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".rar",
  ".zip",
];

export const COMMON_MINE_TYPES = [
  "application/pdf", //pdf
  "application/msword", //doc
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", //doc
  "application/x-ole-storage", //docx
  "application/vnd.ms-excel", //xls
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", //xlsx
  "text/csv", //csv
  "application/vnd.ms-powerpoint", // ppt
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", //pptx
  "text/plain", //txt
  "image/jpg",
  "image/jpeg",
  "image/png",
  "image/gif",
  "application/vnd.rar",
  "application/x-rar-compressed",
  "application/x-rar",
  "multipart/x-rar",
  "application/zip",
  "application/x-zip",
  "application/x-zip-compressed",
];
